import { createFocusTrap } from '@scale/focus-trap';
import { Events } from '@scale/events';

const DECLARATIVE_EVENTS = ['click', 'change', 'submit', 'input', 'keydown', 'keyup', 'focus', 'blur', 'toggle'];
let eventsRegistered = false;

export class ScaleElement extends HTMLElement {
  #controller;
  #refs = new Map();
  #timers = new Map();
  #refObserver;

  static requiredRefs = [];
  static observeRefs = false;

  get signal() {
    return this.#controller.signal;
  }


  connectedCallback() {
    this.#controller = new AbortController();
    this.#validateRefs();
    this.#setupRefObserver();
    this.setup();
    registerDeclarativeEvents();
    if (Shopify?.designMode) this.#setupDesignMode();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#refs.clear();
    this.#clearTimers();
    this.#refObserver?.disconnect();
    this.teardown();
  }

  #reinit() {
    this.#controller.abort();
    this.#refs.clear();
    this.#clearTimers();
    this.#refObserver?.disconnect();
    this.teardown();

    this.#controller = new AbortController();
    this.#validateRefs();
    this.#setupRefObserver();
    this.setup();
    this.#setupDesignMode();
  }

  setup() {}
  teardown() {}
  updatedCallback() {}
  onBlockSelect(e) {}
  onBlockDeselect(e) {}


  ref(name) {
    if (!this.#refs.has(name)) {
      const el = this.querySelector(`[data-ref="${name}"]`);
      if (el) this.#refs.set(name, el);
      return el;
    }
    return this.#refs.get(name);
  }

  refs(name) {
    const key = `${name}[]`;
    if (!this.#refs.has(key)) {
      const els = [...this.querySelectorAll(`[data-ref="${name}"]`)];
      if (els.length) this.#refs.set(key, els);
      return els;
    }
    return this.#refs.get(key);
  }

  invalidateRefs() {
    this.#refs.clear();
  }

  #validateRefs() {
    for (const name of this.constructor.requiredRefs) {
      if (!this.querySelector(`[data-ref="${name}"]`)) {
        throw new Error(`Required ref "${name}" not found in <${this.localName}>`);
      }
    }
  }

  #setupRefObserver() {
    if (!this.constructor.observeRefs) return;

    this.#refObserver = new MutationObserver(() => this.invalidateRefs());
    this.#refObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-ref'],
    });
  }


  on(event, handler, opts = {}) {
    const { target, ...listenerOpts } = opts;
    (target || this).addEventListener(event, handler, { ...listenerOpts, signal: this.signal });
  }

  onDocument(event, handler) {
    document.addEventListener(event, handler, { signal: this.signal });
  }

  onVariantSelected(handler) {
    const { productId } = this.dataset;
    if (!productId) throw new Error(`<${this.localName}> listens to variant changes without data-product-id`);
    this.onDocument(Events.VARIANT_SELECTED, (e) => {
      if (e.productId === productId) handler(e);
    });
  }

  emit(name, detail) {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true, detail }));
  }

  broadcast(event) {
    document.dispatchEvent(event);
  }


  mediaQuery(query, callback) {
    const mq = window.matchMedia(query);
    const handler = (e) => callback(e.matches);
    mq.addEventListener('change', handler, { signal: this.signal });
    callback(mq.matches);
  }

  get isMobile() {
    return !window.matchMedia('(width >= 750px)').matches;
  }


  onScreen(callback, threshold = 0) {
    const watch = screenWatch(threshold);
    watch.callbacks.set(this, callback);
    watch.observer.observe(this);
    const stop = () => {
      if (watch.callbacks.get(this) !== callback) return;
      watch.callbacks.delete(this);
      watch.observer.unobserve(this);
      if (watch.callbacks.size) return;
      watch.observer.disconnect();
      screenWatches.delete(threshold);
    };
    this.signal.addEventListener('abort', stop, { once: true });
    return stop;
  }


  debounce(key, fn, wait = 300) {
    const existing = this.#timers.get(key);
    if (existing) clearTimeout(existing);
    this.#timers.set(
      key,
      setTimeout(() => {
        this.#timers.delete(key);
        if (!this.signal.aborted) fn.call(this);
      }, wait)
    );
  }

  #clearTimers() {
    for (const id of this.#timers.values()) clearTimeout(id);
    this.#timers.clear();
  }


  getBoolean(name) {
    const val = this.getAttribute(name);
    return val !== null && val !== 'false';
  }

  getNumber(name, fallback = 0) {
    const value = this.getAttribute(name);
    return value === null || value.trim() === '' || Number.isNaN(Number(value)) ? fallback : Number(value);
  }


  announce(text, priority = 'polite') {
    const el = document.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('aria-live', priority);
    el.setAttribute('aria-atomic', 'true');
    el.textContent = text;
    const modals = document.querySelectorAll('dialog:modal');
    (modals[modals.length - 1] ?? document.body).append(el);
    setTimeout(() => el.remove(), 1000);
  }

  trapFocus(container) {
    return createFocusTrap(container, { signal: this.signal });
  }


  #setupDesignMode() {
    const sectionId = this.dataset.sectionId || this.closest('.shopify-section')?.id?.replace('shopify-section-', '');
    const blockId = this.dataset.blockId || this.id?.replace('shopify-block-', '');

    this.onDocument('shopify:section:load', (e) => {
      if (e.detail?.sectionId === sectionId) this.#reinit();
    });

    this.onDocument('shopify:section:unload', (e) => {
      if (e.detail?.sectionId === sectionId) this.teardown();
    });

    this.onDocument('shopify:block:select', (e) => {
      if (e.detail?.blockId === blockId) this.onBlockSelect(e);
    });

    this.onDocument('shopify:block:deselect', (e) => {
      if (e.detail?.blockId === blockId) this.onBlockDeselect(e);
    });
  }
}


const screenWatches = new Map();

function screenWatch(threshold) {
  if (!screenWatches.has(threshold)) {
    const callbacks = new Map();
    const observer = new IntersectionObserver((entries) => {
      for (const { target, isIntersecting } of entries) callbacks.get(target)?.(isIntersecting);
    }, { threshold });
    screenWatches.set(threshold, { observer, callbacks });
  }
  return screenWatches.get(threshold);
}


function registerDeclarativeEvents() {
  if (eventsRegistered) return;
  eventsRegistered = true;

  for (const eventName of DECLARATIVE_EVENTS) {
    const attr = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const target = event.composedPath?.()?.[0] ?? event.target;
        if (!(target instanceof Element)) return;

        const el = target.hasAttribute(attr)
          ? target
          : event.bubbles
            ? target.closest(`[on\\:${eventName}]`)
            : null;
        if (!el) return;

        const method = el.getAttribute(attr);
        if (!method) return;

        const component = findScaleElement(el, method);
        if (!component) return;

        component[method](event);
      },
      { capture: true }
    );
  }
}

function findScaleElement(el, method) {
  let node = el.parentElement;
  while (node) {
    if (node instanceof ScaleElement && typeof node[method] === 'function') return node;
    node = node.parentElement;
  }
  return null;
}
